import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings, AlertCircle } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { Skeleton } from '../../components/ui/Skeleton';
import { api, ApiError } from '../../lib/api';

interface Status {
  node_env: string;
  port: number;
  web_origin: string;
  public_base_url: string | null;
  auth: {
    oidc_configured: boolean;
    dev_login_active: boolean;
    emergency_login_enabled: boolean;
  };
  email: {
    backend: string;
    from: string | null;
    aws_region: string | null;
  };
  integrations: {
    planon_configured: boolean;
  };
  monitoring: {
    sentry_api_enabled: boolean;
    sentry_web_enabled: boolean;
  };
  automation: {
    reminder_sweep_enabled: boolean;
    task_digest_enabled: boolean;
  };
  session: {
    cookie_secure: boolean;
  };
  runtime: {
    node_version: string;
    uptime_seconds: number;
    memory_rss_mb: number;
  };
}

const ALLOWED = ['admin', 'super_admin'];

/**
 * Read-only system status. Shows feature flags and configuration
 * presence so an admin can verify the deployment without SSH access.
 * Sensitive values (secrets, full URLs with creds) are never exposed
 * — only booleans and shapes.
 */
export function SystemStatus() {
  const { data: user, isLoading } = useCurrentUser();
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !user.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<Status>('/api/admin/system/status');
        if (!cancelled) setStatus(r);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        System status is restricted to admin / super_admin.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-surface-inset text-fg-muted">
            <Settings className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-fg-muted">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              System status
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Read-only view of feature flags and configuration. To change
              any of these, redeploy with new env vars; runtime toggling is
              deliberately not supported for security-sensitive flags.
            </p>
          </div>
        </header>

        {error ? (
          <div className="mt-6 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {status ? (
          <div className="mt-6 space-y-4">
            <Panel title="Auth">
              <Row label="OIDC (Entra) configured" value={<Bool v={status.auth.oidc_configured} />} />
              <Row label="Dev login active" value={<Bool v={status.auth.dev_login_active} warnIfTrue />} />
              <Row label="Emergency login enabled" value={<Bool v={status.auth.emergency_login_enabled} warnIfTrue />} />
            </Panel>
            <Panel title="Email">
              <Row label="Backend" value={<Mono>{status.email.backend}</Mono>} />
              <Row label="From address" value={status.email.from ?? <Muted>not set</Muted>} />
              <Row label="AWS region" value={status.email.aws_region ?? <Muted>not set</Muted>} />
            </Panel>
            <Panel title="Integrations">
              <Row label="Planon webhook configured" value={<Bool v={status.integrations.planon_configured} />} />
            </Panel>
            <Panel title="Monitoring">
              <Row label="Sentry (API)" value={<Bool v={status.monitoring.sentry_api_enabled} />} />
              <Row label="Sentry (web)" value={<Bool v={status.monitoring.sentry_web_enabled} />} />
            </Panel>
            <Panel title="Automated emails">
              <Row label="Reminder sweep (daily 09:00)" value={<Bool v={status.automation.reminder_sweep_enabled} />} />
              <Row label="New-task digest" value={<Bool v={status.automation.task_digest_enabled} />} />
            </Panel>
            <Panel title="Web / session">
              <Row label="Web origin" value={<Mono>{status.web_origin}</Mono>} />
              <Row label="Public base URL" value={status.public_base_url ?? <Muted>(uses web origin)</Muted>} />
              <Row label="Session cookie Secure flag" value={<Bool v={status.session.cookie_secure} />} />
            </Panel>
            <Panel title="Runtime">
              <Row label="Environment" value={<Mono>{status.node_env}</Mono>} />
              <Row label="Listen port" value={<Mono>{String(status.port)}</Mono>} />
              <Row label="Node version" value={<Mono>{status.runtime.node_version}</Mono>} />
              <Row label="Uptime (s)" value={<Mono>{String(status.runtime.uptime_seconds)}</Mono>} />
              <Row label="Memory RSS (MB)" value={<Mono>{String(status.runtime.memory_rss_mb)}</Mono>} />
            </Panel>
          </div>
        ) : !error ? (
          <div className="mt-6 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-line/10 bg-surface p-5 shadow-sm"
              >
                <Skeleton className="h-4 w-1/4" />
                <div className="mt-3 space-y-2">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </main>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-fg">
      {children}
    </code>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="border-b border-line/10 px-5 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <dl className="divide-y divide-divider/10 px-5 text-sm">{children}</dl>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <dt className="text-fg-muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Bool({ v, warnIfTrue }: { v: boolean; warnIfTrue?: boolean }) {
  if (v) {
    return (
      <span
        className={`rounded px-2 py-0.5 text-xs font-medium ${
          warnIfTrue
            ? 'bg-warning-soft/15 text-warning-soft-fg'
            : 'bg-success-soft/15 text-success-soft-fg'
        }`}
      >
        on
      </span>
    );
  }
  return (
    <span className="rounded bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg-muted">
      off
    </span>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-fg-dim">{children}</span>;
}
